import React, { useCallback } from "react";
import { Dropdown, ComboBox, Checkbox, TextInput } from "@carbon/react";

const requiredLabel = (text) => (
  <>
    {text} <span style={{ color: "#da1e28" }}>*</span>
  </>
);

const CommonFields = ({
  form,
  touched,
  getFieldError,
  handleBlur,
  updateField,
  getOptions,
}) => {
  const renderDropdown = useCallback(
    (fieldKey, label, required = true) => {
      const items = getOptions(fieldKey) || [];
      const fieldValue = form[fieldKey];
  


      let selectedItem = null;
      if (items?.length && fieldValue !== undefined && fieldValue !== null) {
        selectedItem =
  items.find((i) => Number(i.id) === Number(fieldValue)) ||
  items.find(
    (i) =>
      String(i.label || i.attr_val_name || "").toLowerCase() ===
      String(fieldValue || "").toLowerCase()
  ) ||
  null;
      }

      const shouldShowError = touched[fieldKey];
      const err = shouldShowError ? getFieldError(fieldKey) : null;
      return (
        <Dropdown
          id={fieldKey}
          titleText={required ? requiredLabel(label) : label}
          label="Select"
          items={items}
          itemToString={(item) => item?.label ?? item?.attr_val_name ?? ""}
          selectedItem={selectedItem}
          onChange={({ selectedItem }) =>
            updateField(fieldKey, selectedItem?.id ?? "")
          }
          onBlur={() => handleBlur(fieldKey)}
          invalid={!!err}
          invalidText={err || ""}
        />
      );
    },
    [getOptions, form, touched, getFieldError, updateField, handleBlur]
  );

  // ─────────────────────────────────────────────────────────────
  // COMBOBOX (searchable dropdown — type to filter)
  // ─────────────────────────────────────────────────────────────
  const renderComboBox = useCallback(
    (fieldKey, label, placeholder = "Search...", required = true) => {
      const items = getOptions(fieldKey) || [];
      const fieldValue = form[fieldKey];

      let selectedItem = null;
      if (items?.length && fieldValue !== undefined && fieldValue !== null) {
        selectedItem =
          items.find((i) => Number(i.id) === Number(fieldValue)) ||
          items.find(
            (i) =>
              String(i.label || i.attr_val_name || "").toLowerCase() ===
              String(fieldValue || "").toLowerCase()
          ) ||
          null;
      }

      const shouldShowError = touched[fieldKey];
      const err = shouldShowError ? getFieldError(fieldKey) : null;

      return (
        <ComboBox
          id={fieldKey}
          titleText={required ? requiredLabel(label) : label}
          placeholder={placeholder}
          items={items}
          itemToString={(item) => item?.label ?? item?.attr_val_name ?? ""}
          selectedItem={selectedItem}
          onChange={({ selectedItem }) =>
            updateField(fieldKey, selectedItem?.id ?? "")
          }
          onBlur={() => handleBlur(fieldKey)}
          invalid={!!err}
          invalidText={err || ""}
          shouldFilterItem={({ item, inputValue }) => {
            if (!inputValue) return true;
            const label = (item?.label ?? item?.attr_val_name ?? "").toLowerCase();
            return label.includes(inputValue.toLowerCase());
          }}
          autoComplete="off"
        />
      );
    },
    [getOptions, form, touched, getFieldError, updateField, handleBlur]
  );


  const renderNumberField = useCallback((fieldKey, label, placeholder = 0, required = false) => {
    const shouldShowError = touched[fieldKey];
    const err = shouldShowError ? getFieldError(fieldKey) : null;
    
    return (
      <div>
        <TextInput
          id={fieldKey}
          labelText={required ? requiredLabel(label) : label}  
          value={form[fieldKey] || ""}
          onChange={(e) => {
            const value = e.target.value;
            if (value === "" || /^\d*\.?\d*$/.test(value)) {
              updateField(fieldKey, value);
            }
          }}
          onBlur={() => handleBlur(fieldKey)}
          invalid={!!err}
          invalidText={err || ""}
          style={{ width: "100%" }}
          type="text"
          inputMode="numeric"
          placeholder={placeholder}
        />
      </div>
    );
  }, [form, touched, getFieldError, handleBlur, updateField]);

  // CHECKBOX
  const renderCheckbox = useCallback(
    (fieldKey, label) => (
      <Checkbox
        id={fieldKey}
        labelText={label}
        checked={!!form[fieldKey]}
        onChange={(e) => updateField(fieldKey, e.target.checked)}
        onBlur={() => handleBlur(fieldKey)}
      />
    ),
    [form, updateField, handleBlur]
  );

  // TEXT INPUT
  const renderTextInput = useCallback(
    (fieldKey, label, placeholder = "", required = true) => {
      const shouldShowError = touched[fieldKey];
      const err = shouldShowError ? getFieldError(fieldKey) : null;

      return (
        <TextInput
          id={fieldKey}
          labelText={required ? requiredLabel(label) : label}
          value={form[fieldKey] || ""}
          onChange={(e) => updateField(fieldKey, e.target.value)}
          onBlur={() => handleBlur(fieldKey)}
          invalid={!!err}
          invalidText={err || ""}
          placeholder={placeholder}
        />
      );
    },
    [form, touched, getFieldError, updateField, handleBlur]
  );

  return {
    renderDropdown,
    renderComboBox,
    renderNumberField,
    renderCheckbox,
    renderTextInput,
  };
};

export default CommonFields;